import { createPortal, useEffect, useState } from "preact/compat";
import constants from "../constants";
import locationService from "../service/locationService";
import dashboardService from "../service/dashboardService";
import LoadingSpinner from "../component/LoadingSpinner";
import MOBackIcon from "../component/svg/MOBackIcon";
import { Button, message } from "antd";
import store from '../store';
import util from "../util";
import { setIsDSADesign } from "../actions";
import qrCodeService from "../service/qrCodeService";

export interface Props {
  open: boolean;
  onClose: () => void;
}

export default function ShareToDisplayModal({ open, onClose }: Props) {
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("pages");
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [screens, setScreens] = useState([]);
  const [filteredScreens, setFilteredScreens] = useState([]);

  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [selectedLocation, setSelectedLocation] = useState<any>(null);
  const [selectedScreens, setSelectedScreens] = useState<any>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const cacheBust = (new Date()).getTime();

  useEffect(() => {
    if (util.getNumberOfPages() > 1) {
      setView("pages")
    } else if (util.getNumberOfPages() > 1) {
      setView("locations")
    }
  }, [open]);

  useEffect(() => {
    setLoading(true);

    dashboardService.getMemberLocations(store.getState().memberId).then(resp => {
      setLocations(resp.data || []);
      setFilteredLocations(resp.data || []);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    setFilteredLocations(locations.filter((location: any) => location.name.toLowerCase().includes(searchTerm.toLowerCase())));
  }, [searchTerm, locations]);

  useEffect(() => {
    setFilteredScreens(screens.filter((screen: any) => screen.name.toLowerCase().includes(searchTerm.toLowerCase())));
  }, [searchTerm, screens]);

  function selectPage(page: number) {
    setSelectedPage(page);
    setView("locations");
  }

  function selectLocation(location: any) {
    setSelectedLocation(location);
    setLoading(true);
    setView("screens");

    dashboardService.getScreensPerLocation(location.internalId).then(resp => {
      setScreens(resp.data || []);
      setFilteredScreens(resp.data || []);
      setLoading(false);
    });
  }

  function selectScreen(screen: any) {
    if (isScreenSelected(screen)) {
      setSelectedScreens(selectedScreens.filter((selectedScreen: any) => selectedScreen.serial !== screen.serial));
    } else {
      setSelectedScreens([screen, ...selectedScreens]);
    }
  }

  function backToLocations() {
    setSelectedLocation(null);
    setView("locations");
    setSearchTerm('');
  }

  function backToPages() {
    setSelectedPage(1);
    setView("pages");
    setSearchTerm('');
  }

  function isScreenSelected(screen: any) {
    return selectedScreens.some((s: { serial: any; }) => s.serial === screen.serial);
  }

  function share() {
    if (selectedScreens.length == 0) return;

    setLoading(true);

    dashboardService.createBoard({
      type: "DESIGN",
      designId: store.getState().designId,
      page: selectedPage,
      name: store.getState().designName,
      memberId: store.getState().memberId,
      locationId: selectedLocation.internalId
    }).then((resp) => {
      const board = resp.data;
      console.log("> board: " + board)

      qrCodeService.createOrUpdateShareLink(store.getState().designId, constants.SL_TYPE_DSA).then(() => {
        selectedScreens.forEach((screen: any) => {
          const dto = {
            type: "BOARD",
            sourceId: board.id,
            playable: true,
          };

          dashboardService.setScreenContent(screen.serial, dto).then((resp2) => {
            console.log(resp2.data)
            setView("locations");
            setLoading(false);
            setSelectedLocation(null);
            setSelectedScreens([]);
            onClose();
            store.dispatch(setIsDSADesign(true));
            message.success(`Your design has been successfully shared`);

            const shareDropdownObj = $('.dropdown-toggle.publish-first');
            shareDropdownObj.trigger('click');
          });
        });
      });
    });
  }

  return open ? createPortal(<div>
    <div className="modal-back" onClick={onClose} />
    <div className="modal share-to-display" onClick={e => e.stopPropagation()}>
      {view == "screens" &&
        <div className="back-btn" onClick={backToLocations}>
          <MOBackIcon />
        </div>
      }

      {view == "locations" && util.getNumberOfPages() > 1 &&
        <div className="back-btn" onClick={backToPages}>
          <MOBackIcon />
        </div>
      }

      <a className="close" onClick={onClose}>
        <svg>
          <use xlinkHref={`${constants.appPrefix}/svg/icons.svg#app-close`} />
        </svg>
      </a>

      <header>
        <h2>Share to Display</h2>
      </header>

      <div className="content">
        {loading ? <LoadingSpinner /> :
          <>
            {view == "pages" ?
              <>
                <p>Choose which page of design to share:</p>
                
                <div className="pages">
                  {[...Array(util.getNumberOfPages())].map((item, i) =>
                    <div onClick={() => { selectPage(i + 1) }}>
                      <img src={`${constants.RENDERER_DOMAIN}/design/thumbnail/${store.getState().designId}?height=300&update=${cacheBust}&page=${i + 1}`} />

                      <p>{store.getState().designName}</p>
                      <small>Page {i + 1}</small>
                    </div>
                  )}
                </div>

              </>
              : view == "locations" ?
                <>
                  {locations.length > 1 &&
                    <input
                      type="text"
                      className="search-by-location"
                      placeholder="Search by location name"
                      value={searchTerm}
                      onInput={e => setSearchTerm((e.target as HTMLInputElement).value)}
                    />
                  }

                  <p>Choose location:</p>

                  <div className="locations">
                    {filteredLocations.map((loc: any) =>
                      <div className="location" onClick={() => selectLocation(loc)}>
                        <div>{loc.name}</div>
                        <MOBackIcon />
                      </div>
                    )}
                  </div>
                </>
                :
                <>
                  {screens.length > 1 &&
                    <input
                      type="text"
                      className="search-by-location"
                      placeholder="Search by screen name"
                      value={searchTerm}
                      onInput={e => setSearchTerm((e.target as HTMLInputElement).value)}
                    />
                  }

                  <div style={{ width: "100%" }}>
                    <h4><b>{selectedLocation ? selectedLocation.name : ""}</b></h4>

                    <div className="screens">
                      {!filteredScreens.length && <p>There are no screens for this location.</p>}
                      {filteredScreens.map((screen: any) =>
                        <div className={`screen ${isScreenSelected(screen) ? "selected" : ""}`} onClick={() => selectScreen(screen)}>
                          <div>{screen.name}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
            }
          </>
        }

        {view == "screens" && <Button type="primary" className={`btn primary ${selectedScreens.length === 0 ? "disabled" : ""}`} disabled={selectedScreens.length === 0} onClick={share}>Share</Button>}
      </div>
    </div>
  </div>, document.getElementById('modals')!) : null;
}
